<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import ButtonItem from './components/ButtonItem.vue';
import LanguageSelect from './components/LanguageSelect.vue';
import CategorySelect from './components/CategorySelect.vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { watch } from 'vue';
import PhoneMock from './components/PhoneMock.vue';
import ImageUploader from './components/ui/ImageUploader.vue';

const props = defineProps({
  channelId: {
    type: String,
    required: true,
  },
});
const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const router = useRouter();

const selectedFile = ref(null);

const config = ref({
  name: '',
  category: '',
  parameter_format: '',
  allow_category_change: true,
  language: '',
  content: {
    body: '',
    footer: '',
  },
});

const buttons = ref([]);

const buttonBuilder = items => {
  const buttonsMapped = items.map(button => {
    if (button.type === 'URL') {
      return {
        type: 'URL',
        text: button.text,
        url: button.url,
      };
    }

    if (button.type === 'PHONE_NUMBER') {
      return {
        type: 'PHONE_NUMBER',
        text: button.text,
        phone_number: button.phone_number,
      };
    }

    if (button.type === 'QUICK_REPLY') {
      return {
        type: 'QUICK_REPLY',
        text: button.text,
      };
    }

    if (button.type === 'COPY_CODE') {
      return {
        type: 'COPY_CODE',
        example: button.example,
      };
    }
    return undefined;
  });

  if (buttonsMapped.length === 0) {
    return undefined;
  }

  return {
    type: 'BUTTONS',
    buttons: [...buttonsMapped],
  };
};

const validation = reactive({
  name: { isValid: true, message: '' },
  category: { isValid: true, message: '' },
  message: { isValid: true, message: '' },
  language: { isValid: true, message: '' },
});

const hasNoSpaces = value => /^\S+$/.test(value);

const validateForm = () => {
  let isValid = true;

  // Name validation
  if (!config.value.name.trim()) {
    validation.name.isValid = false;
    validation.name.message = t(
      'WHATSAPP_TEMPLATES.ADD.VALIDATION.NAME_REQUIRED'
    );
    isValid = false;
  } else if (!hasNoSpaces(config.value.name)) {
    validation.name.isValid = false;
    validation.name.message = t(
      'WHATSAPP_TEMPLATES.ADD.VALIDATION.NAME_NO_SPACES'
    );
    isValid = false;
  } else {
    validation.name.isValid = true;
  }

  // Category validation
  if (!config.value.category) {
    validation.category.isValid = false;
    validation.category.message = t(
      'WHATSAPP_TEMPLATES.ADD.VALIDATION.CATEGORY_REQUIRED'
    );
    isValid = false;
  } else {
    validation.category.isValid = true;
  }

  // Message validation
  if (!config.value.content.body.trim()) {
    validation.message.isValid = false;
    validation.message.message = t(
      'WHATSAPP_TEMPLATES.ADD.VALIDATION.MESSAGE_REQUIRED'
    );
    isValid = false;
  } else {
    validation.message.isValid = true;
  }

  // Language validation
  if (!config.value.language) {
    validation.language.isValid = false;
    validation.language.message = t(
      'WHATSAPP_TEMPLATES.ADD.VALIDATION.LANGUAGE_REQUIRED'
    );
    isValid = false;
  } else {
    validation.language.isValid = true;
  }

  return isValid;
};

watch(
  () => config.value,
  () => {
    validateForm();
  },
  { deep: true }
);

const SubmitForm = async () => {
  if (!validateForm()) return;

  const buildedButtons = buttonBuilder(buttons.value);

  const footer = config.value.content.footer
    ? {
        type: 'FOOTER',
        text: config.value.content.footer,
      }
    : undefined;

  const data = {
    name: config.value.name,
    language: config.value.language,
    category: config.value.category,
    image: selectedFile.value,
    components: [
      {
        type: 'BODY',
        text: config.value.content.body,
      },
    ],
  };

  if (buildedButtons) {
    data.components.push(buildedButtons);
  }

  if (footer) {
    data.components.push(footer);
  }

  try {
    await store.dispatch('messageTemplates/createTemplate', {
      channelId: props.channelId,
      templateData: data,
    });

    useAlert(t('WHATSAPP_TEMPLATES.ADD.API.SUCCESS'));
    router.go(-1);
  } catch (error) {
    useAlert(`${t('WHATSAPP_TEMPLATES.ADD.API.ERROR')} - ${error.message}`);
  }
};

const NewButton = () => {
  buttons.value.push({
    type: 'URL',
    text: '',
    url: '',
    phone_number: '',
    example: '',
  });
};

const updateButton = (index, updatedButton) => {
  buttons.value[index] = updatedButton;
};

const deleteButton = index => {
  buttons.value.splice(index, 1);
};

const uiFlags = computed(() => getters['messageTemplates/getUIFlags'].value);
</script>

<template>
  <div class="p-2 md:p-6 min-h-full">
    <BaseSettingsHeader :title="$t('WHATSAPP_TEMPLATES.ADD.HEADER')" />
    <div class="md:grid md:grid-cols-2">
      <div class="w-full">
        <form @submit.prevent="SubmitForm">
          <div class="flex flex-col">
            <div class="flex w-full gap-3 justify-between flex-col md:flex-row">
              <div class="w-full">
                <label>
                  {{ $t('WHATSAPP_TEMPLATES.ADD.FIELDS.NAME.LABEL') }}
                  <input
                    v-model="config.name"
                    type="text"
                    :class="{ 'border-red-500': !validation.name.isValid }"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATES.ADD.FIELDS.NAME.PLACEHOLDER')
                    "
                    @input="validateForm"
                  />
                  <span
                    v-if="!validation.name.isValid"
                    class="text-red-500 text-sm"
                  >
                    {{ validation.name.message }}
                  </span>
                </label>
              </div>

              <div class="w-full flex flex-col">
                <CategorySelect
                  v-model="config.category"
                  class="w-full"
                  :class="{ 'border-red-500': !validation.category.isValid }"
                />
                <span
                  v-if="!validation.category.isValid"
                  class="text-red-500 text-sm"
                >
                  {{ validation.category.message }}
                </span>

                <label class="flex items-center space-x-2 mt-2">
                  <woot-switch
                    id="sla_bh"
                    v-model="config.allow_category_change"
                  />
                  <span>{{
                    $t(
                      'WHATSAPP_TEMPLATES.ADD.FIELDS.ALLOW_CATEGORY_CHANGE.LABEL'
                    )
                  }}</span>
                </label>
              </div>
            </div>

            <div>
              <label>
                {{ $t('WHATSAPP_TEMPLATES.ADD.FIELDS.MESSAGE.LABEL') }}
                <textarea
                  v-model="config.content.body"
                  :class="{ 'border-red-500': !validation.message.isValid }"
                  :placeholder="
                    $t('WHATSAPP_TEMPLATES.ADD.FIELDS.MESSAGE.PLACEHOLDER')
                  "
                  class="min-h-32"
                />
                <span
                  v-if="!validation.message.isValid"
                  class="text-red-500 text-sm"
                >
                  {{ validation.message.message }}
                </span>
              </label>
            </div>

            <div class="mb-4">
              <label>
                {{ $t('WHATSAPP_TEMPLATES.ADD.FIELDS.IMAGE.LABEL') }}
                <ImageUploader v-model="selectedFile" />
              </label>
            </div>

            <div class="flex gap-2 w-full">
              <label class="w-full">
                {{ $t('WHATSAPP_TEMPLATES.ADD.FIELDS.FOOTER.LABEL') }}
                <input
                  v-model="config.content.footer"
                  type="text"
                  :placeholder="
                    $t('WHATSAPP_TEMPLATES.ADD.FIELDS.FOOTER.PLACEHOLDER')
                  "
                />
              </label>

              <div class="w-full">
                <LanguageSelect
                  v-model="config.language"
                  class="w-full"
                  :class="{ 'border-red-500': !validation.language.isValid }"
                />
                <span
                  v-if="!validation.language.isValid"
                  class="text-red-500 text-sm"
                >
                  {{ validation.language.message }}
                </span>
              </div>
            </div>

            <div class="">
              <div class="flex justify-between">
                <p class="text-xl text-slate-200">
                  {{
                    $t('WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.HEADER.MAIN')
                  }}
                  <span class="text-slate-400">
                    {{
                      $t(
                        'WHATSAPP_TEMPLATES.ADD.ADD_BUTTONS_SECTION.HEADER.OPTIONAL'
                      )
                    }}
                  </span>
                </p>
                <woot-submit-button
                  button-class="p-2"
                  icon-class="add-circle"
                  type="button"
                  @click="NewButton"
                />
              </div>
              <div class="">
                <ButtonItem
                  v-for="(button, index) in buttons"
                  :key="index"
                  :button="button"
                  @update:button="updateButton(index, $event)"
                  @delete:button="deleteButton(index)"
                />
              </div>
            </div>
            <div class="mt-6">
              <woot-submit-button
                button-class="mt-2 inline-flex justify-center w-16"
                :disabled="uiFlags.isCreating"
                :loading="uiFlags.isCreating"
                :button-text="t('WHATSAPP_TEMPLATES.ADD.BUTTONS.SUBMIT')"
              />
            </div>
          </div>
        </form>
      </div>
      <div
        class="invisible md:visible w-full h-full flex items-center justify-center"
      >
        <PhoneMock
          :img="selectedFile"
          :body="config.content.body"
          :footer="config.content.footer"
          :buttons="buttons"
        />
      </div>
    </div>
  </div>
</template>
